import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import toColorString from 'polished/lib/color/toColorString';
import color from '@decisiv/design-tokens/lib/color';

import Modal from '@decisiv/ui-components/lib/components/Modal';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import { Checkbox } from '@decisiv/ui-components';
import { H4, P } from '@decisiv/ui-components/lib/components/Typography';
import { t, Trans } from '@lingui/macro';
import Share from '@decisiv/iconix/lib/components/Share';
import ArrowRight from '@decisiv/iconix/lib/components/ArrowRight';
import ChevronRight from '@decisiv/iconix/lib/components/ChevronRight';
import WrapperWithLoading from '../WrapperWithLoading';
import getEnvironment from '../../utils/getCurrentEnv';
import {
  parseAttributeKey,
  getHeaderValue,
} from '../../features/dynamic-form/core/attributeKey';

import {
  AttributeContent,
  AttributeRow,
  BindAllRow,
  ChevronWrapper,
  CountBadge,
  DescriptionText,
  IntegrationCard,
  IntegrationCardHeader,
  ModalSourceBlock,
  ModalWidthOverride,
  NewValuePill,
  ValuePill,
} from './style';

// Stable default: a fresh literal here would change identity every render
// and re-fire the hooks that depend on it.
const NO_SHARED_SETTINGS = [];

const pacificOcean = toColorString(color.interaction.pacificOcean);
const environment = getEnvironment(process.env.REACT_APP_BASE_URL);

// Build a map keyed by target attribute name, containing only attributes
// that have an explicit mapping in a shared setting. Same-name matching is
// intentionally excluded — propagation must be driven by shared settings only.
// Handles both directions: if the shared setting was defined as A→B but the
// user is propagating from B, we swap the attribute names so B→A still works.
const buildInitialBindingMap = (
  targets,
  sourceIntegrationUuid,
  allSharedSettings,
) => {
  const map = {};
  targets.forEach((target) => {
    map[target.id] = {};
    const targetUuid = target.attributes.integration_uuid;
    const sharedSetting =
      allSharedSettings?.find(
        (s) =>
          (s.integration_uuids || []).includes(sourceIntegrationUuid) &&
          (s.integration_uuids || []).includes(targetUuid),
      ) || null;

    const settingMappings = sharedSetting?.mappings || [];

    // Forward: mapping was defined source → target
    settingMappings
      .filter(
        (m) =>
          m.from_integration_uuid === sourceIntegrationUuid &&
          m.to_integration_uuid === targetUuid,
      )
      .forEach((m) => {
        const { baseName: toBase } = parseAttributeKey(m.to_attribute_name);
        map[target.id][m.to_attribute_name] = {
          bound: true,
          bindable: true,
          sourceAttrName: m.from_attribute_name,
          targetAttrBaseName: toBase,
        };
      });

    // Reverse: mapping was defined target → source, so swap attr names
    settingMappings
      .filter(
        (m) =>
          m.from_integration_uuid === targetUuid &&
          m.to_integration_uuid === sourceIntegrationUuid,
      )
      .forEach((m) => {
        const { baseName: fromBase } = parseAttributeKey(m.from_attribute_name);
        map[target.id][m.from_attribute_name] = {
          bound: true,
          bindable: true,
          sourceAttrName: m.to_attribute_name,
          targetAttrBaseName: fromBase,
        };
      });
  });
  return map;
};

const formatValue = (value) => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  const str = String(value);
  return str === '' ? '—' : str;
};

export default function PropagateSettingsModal({
  visible,
  onClose,
  onConfirm,
  sourceAttributes,
  targetConfiguredIntegrations,
  targetIntegrationNames = {},
  sourceIntegrationName = '',
  sourceIntegrationUuid = '',
  sourceDealerName = '',
  sourceSrmAccountId = '',
  allSharedSettings = NO_SHARED_SETTINGS,
  loading = false,
  saving = false,
}) {
  const [bindingMap, setBindingMap] = useState({});
  const [expandedIds, setExpandedIds] = useState([]);

  useEffect(() => {
    if (!visible || !targetConfiguredIntegrations.length) return;
    setBindingMap(
      buildInitialBindingMap(
        targetConfiguredIntegrations,
        sourceIntegrationUuid,
        allSharedSettings,
      ),
    );
    setExpandedIds([]);
  }, [
    visible,
    targetConfiguredIntegrations,
    sourceIntegrationUuid,
    allSharedSettings,
  ]);

  const toggleAccordion = (id) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleBound = (targetId, attrName) => {
    setBindingMap((prev) => ({
      ...prev,
      [targetId]: {
        ...prev[targetId],
        [attrName]: {
          ...prev[targetId]?.[attrName],
          bound: !prev[targetId]?.[attrName]?.bound,
        },
      },
    }));
  };

  const toggleAllBound = (targetId, attrNames, newBound) => {
    setBindingMap((prev) => {
      const updated = { ...prev[targetId] };
      attrNames.forEach((attrName) => {
        if (updated[attrName]) {
          updated[attrName] = { ...updated[attrName], bound: newBound };
        }
      });
      return { ...prev, [targetId]: updated };
    });
  };

  const loadingTitle = saving
    ? t`Saving...`
    : sourceIntegrationName || t`Loading configured locations...`;

  let loadingDescription = '';
  if (saving) {
    loadingDescription = t`Applying settings to all linked locations. Please wait.`;
  } else if (sourceDealerName && sourceSrmAccountId) {
    loadingDescription = `Loading configured locations for ${sourceDealerName} (${sourceSrmAccountId})...`;
  }

  const noTargets = !loading && targetConfiguredIntegrations.length === 0;

  return (
    <>
      <ModalWidthOverride />
      <Modal
        actions={[
          {
            text: t`Save All`,
            onClick: () => onConfirm(bindingMap),
            disabled: loading || saving,
          },
          {
            text: t`Cancel`,
            onClick: onClose,
            disabled: saving,
          },
        ]}
        icon={Share}
        id="propagate-settings-modal"
        intent="information"
        onClose={onClose}
        title={t`Save and Propagate Settings`}
        visible={visible}
      >
        <WrapperWithLoading
          loading={loading || saving}
          title={loadingTitle}
          description={loadingDescription}
        >
          <Flex flexDirection="column">
            <ModalSourceBlock>
              <H4 weight="extrabold" marginBottom={0}>
                {sourceIntegrationName || '—'}
              </H4>
              <P weight="semibold" marginBottom={0}>
                {sourceDealerName}
                {sourceSrmAccountId && ` (${sourceSrmAccountId})`}
              </P>
              <Flex alignItems="center">
                <P color="charcoal" weight="semibold" size="small">
                  <Trans>Environment:</Trans>&nbsp;
                </P>
                <P weight="regular" color="alaskanHusky" size="small">
                  {environment}
                </P>
              </Flex>
            </ModalSourceBlock>

            {noTargets ? (
              <DescriptionText>
                <Trans>
                  No other configured locations are linked to this one through
                  Shared Settings, so there is nothing to propagate to. Saving
                  updates this location only.
                </Trans>
              </DescriptionText>
            ) : (
              <DescriptionText>
                <Trans>
                  Check the box next to each attribute you want to overwrite in
                  the other configured locations. Only attributes defined in
                  Shared Settings mappings are shown.
                </Trans>
              </DescriptionText>
            )}

            {targetConfiguredIntegrations.map((target) => {
              const integrationName =
                targetIntegrationNames[target.attributes.integration_uuid] ||
                target.attributes.integration_uuid;
              const locationName = target.attributes.name;
              const srmAccountId = target.attributes.srm_account_id;
              const mappedAttrNames = Object.keys(bindingMap[target.id] || {});
              const boundCount = mappedAttrNames.filter(
                (attrName) => bindingMap[target.id]?.[attrName]?.bound,
              ).length;
              const allBound =
                mappedAttrNames.length > 0 &&
                boundCount === mappedAttrNames.length;
              const isExpanded = expandedIds.includes(target.id);

              return (
                <IntegrationCard key={target.id}>
                  <IntegrationCardHeader
                    onClick={() => toggleAccordion(target.id)}
                  >
                    <ChevronWrapper expanded={isExpanded ? 1 : 0}>
                      <ChevronRight color={pacificOcean} size="small" />
                    </ChevronWrapper>
                    <Flex flexDirection="column" flex={1} marginLeft={1}>
                      <H4 marginBottom={0}>{integrationName}</H4>
                      <P shade={1} size="small">
                        {locationName} ({srmAccountId})
                      </P>
                    </Flex>
                    <CountBadge allBound={allBound ? 1 : 0}>
                      {allBound
                        ? t`All ${mappedAttrNames.length} attributes`
                        : `${boundCount}/${mappedAttrNames.length} attributes`}
                    </CountBadge>
                  </IntegrationCardHeader>

                  <AttributeContent expanded={isExpanded ? 1 : 0}>
                    {mappedAttrNames.length === 0 ? (
                      <BindAllRow>
                        <P size="small" shade={1} marginBottom={0}>
                          <Trans>
                            No shared setting mappings found for this
                            integration.
                          </Trans>
                        </P>
                      </BindAllRow>
                    ) : (
                      <>
                        <BindAllRow>
                          <Checkbox
                            key={`${target.id}-all-${boundCount}`}
                            name={`${target.id}-all`}
                            label={allBound ? t`Unbind all` : t`Bind all`}
                            defaultChecked={allBound}
                            onChange={() =>
                              toggleAllBound(
                                target.id,
                                mappedAttrNames,
                                !allBound,
                              )
                            }
                          />
                        </BindAllRow>

                        {mappedAttrNames.map((attrName) => {
                          const binding =
                            bindingMap[target.id]?.[attrName] || {};
                          const { subKey: targetSubKey } =
                            parseAttributeKey(attrName);
                          const targetAttrBase =
                            binding.targetAttrBaseName || attrName;
                          const targetAttr = (
                            target.attributes.location_settings_attributes || []
                          ).find((a) => a.name === targetAttrBase);
                          const { baseName: srcBase, subKey: srcSubKey } =
                            parseAttributeKey(binding.sourceAttrName || '');
                          const sourceBaseAttr = sourceAttributes.find(
                            (s) => s.name === srcBase,
                          );
                          const currentDisplayValue =
                            targetSubKey !== null &&
                            Array.isArray(targetAttr?.value)
                              ? getHeaderValue(targetAttr.value, targetSubKey)
                              : targetAttr?.value;
                          const newDisplayValue =
                            srcSubKey !== null &&
                            Array.isArray(sourceBaseAttr?.value)
                              ? getHeaderValue(sourceBaseAttr.value, srcSubKey)
                              : sourceBaseAttr?.value;

                          return (
                            <AttributeRow
                              key={attrName}
                              bound={binding.bound ? 1 : 0}
                            >
                              <Flex
                                alignItems="flex-start"
                                style={{ flexShrink: 0 }}
                              >
                                <Checkbox
                                  key={`${target.id}-${attrName}-${binding.bound}`}
                                  name={`${target.id}-${attrName}-bound`}
                                  label={attrName}
                                  defaultChecked={!!binding.bound}
                                  onChange={() =>
                                    toggleBound(target.id, attrName)
                                  }
                                />
                              </Flex>

                              <Flex
                                alignItems="center"
                                flex={1}
                                marginLeft={2}
                                flexWrap="wrap"
                              >
                                <P
                                  size="small"
                                  shade={1}
                                  style={{ marginRight: '4px' }}
                                >
                                  <Trans>Current:</Trans>
                                </P>
                                <ValuePill
                                  title={formatValue(currentDisplayValue)}
                                >
                                  {formatValue(currentDisplayValue)}
                                </ValuePill>

                                {binding.bound &&
                                  newDisplayValue !== undefined && (
                                    <Flex alignItems="center" marginLeft={1}>
                                      <ArrowRight
                                        style={{
                                          width: '14px',
                                          height: '14px',
                                          marginRight: '4px',
                                        }}
                                      />
                                      <NewValuePill
                                        title={formatValue(newDisplayValue)}
                                      >
                                        {formatValue(newDisplayValue)}
                                      </NewValuePill>
                                    </Flex>
                                  )}
                              </Flex>
                            </AttributeRow>
                          );
                        })}
                      </>
                    )}
                  </AttributeContent>
                </IntegrationCard>
              );
            })}
          </Flex>
        </WrapperWithLoading>
      </Modal>
    </>
  );
}
PropagateSettingsModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  sourceAttributes: PropTypes.arrayOf(PropTypes.object).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  targetConfiguredIntegrations: PropTypes.arrayOf(PropTypes.object).isRequired,
  targetIntegrationNames: PropTypes.objectOf(PropTypes.string),
  sourceIntegrationName: PropTypes.string,
  sourceIntegrationUuid: PropTypes.string,
  sourceDealerName: PropTypes.string,
  sourceSrmAccountId: PropTypes.string,
  allSharedSettings: PropTypes.arrayOf(
    PropTypes.shape({
      integration_uuids: PropTypes.arrayOf(PropTypes.string),
      // eslint-disable-next-line react/forbid-prop-types
      mappings: PropTypes.arrayOf(PropTypes.object),
    }),
  ),
  loading: PropTypes.bool,
  saving: PropTypes.bool,
};
